import { EventEmitter, Inject, Injectable } from '@angular/core';
import { Route, Router, Routes } from '@angular/router';

import { DEPLOY_URL, ENV, EnvironmentInterface, PRODUCT, PRODUCT_ROUTES } from '@core/interface/environment.interface';
import { BehaviorSubject } from 'rxjs';
import { StepsSchema } from '../types/schema/steps-schema.type';
import { StepSchema } from '../types/schema/step-schema.type';
import { Step } from '../types/step.type';
import { Steps } from '../types/steps.type';
import { StepForm } from '../types/schema/step-form.type';
import { Widgets } from '@shared/modules/widgets/types/widgets.type';
import { StepFormProperties } from '../types/schema/step-form-properties.type';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { Widget } from '@shared/modules/widgets/types/widget.type';
import { StepFormProperty } from '../types/schema/step-form-property.type';
import { StepFormSessionValues } from '../types/schema/step-form-session-values.type';
import { Address } from '../types/address.type';
import { ProductEnum } from 'environments/products/product.enum';
import { RouteDataType } from '../types/schema/route-data.type';
import { City } from '../types/city.type';
import { StrKeyValType } from '@shared/type/str-key-val.type';

@Injectable({
    providedIn: 'root'
})
export class StepFormService {

    private _currentStep!: Step;
    private currentStepIndex: number = 0;

    private _steps: Array<Step> = [];

    private _documentUrl: string | false = false;
    private _claimNumber: string = '';
    private _propertyDamage: StrKeyValType | undefined = {};
    private _unUploadedDocuments: number = 0;

    public nextStepEvent = new EventEmitter<boolean>(false);
    public previousStepEvent = new EventEmitter<boolean>(false);
    public onSubmit = new EventEmitter<boolean>(false);

    public stepsReady = new BehaviorSubject<boolean>(false);
    public contentReady = new BehaviorSubject<boolean>(false);
    public redirectedFromProperty = false;
    public sessId: string = '';

    public originSteps: Array<Step> = [];
    public propertyDamageSteps: Array<Step> = [];
    public sessionPd?: StrKeyValType;
    public backFromLiability: boolean = false;

    constructor(
        @Inject(DEPLOY_URL) private _deployUrl: string,
        @Inject(ENV) private environment: EnvironmentInterface,
        @Inject(PRODUCT_ROUTES) public productRoutes: Routes,
        @Inject(PRODUCT) public product: ProductEnum,
        private router: Router
    ) {
        let pds = localStorage.getItem('pds');
        let pdse = localStorage.getItem('pdse');
        if (pds && pdse) {
            pds = JSON.parse(pds);
            this.propertyDamageSteps = pds as unknown as Array<Step>;
            //localStorage.removeItem('pds');

            pdse = JSON.parse(pdse);
            this.sessionPd = pdse as unknown as StrKeyValType;
        }

        let bfL = localStorage.getItem('backFromLiability');
        if (bfL == '1') {
            localStorage.removeItem('backFromLiability');
            this.backFromLiability = true;
            this.redirectedFromProperty = false;
        }

    }

    public getStepIndexBySelector(selector: string): number {
        return this.productRoutes.findIndex((route: Route) => {
            return (route.data as RouteDataType).stepSelector == selector;
        });
    }

    get phonePrefix(): string {
        return this.environment.langInstanceConfig.phonePrefix;
    }

    get phoneValidationHint(): string {
        return this.environment.langInstanceConfig.phoneValidationHint;
    }

    getStepByStepSelector(stepSelector: string): Step | undefined {
        return this.steps.find(step => {
            return step.stepSelector === stepSelector;
        });
    }

    /**
     * Correctly redirect to lazy loaded module step
     * @param stepSlug
     * @returns
     */
    goToStep(stepSlug: string): void {
        this.contentReady = new BehaviorSubject<boolean>(false);

        this.router.navigate([
            this.environment.productsConfig[this.product].productUrl,
            stepSlug
        ]);
    }

    /**
     *
     * @param stepsSchema
     */
    initSteps(stepsSchema: StepsSchema) {
        let subSteps: Steps = {};

        for (const route of this.productRoutes) {
            const data = route.data as RouteDataType;
            const schemaStep = stepsSchema.steps.find((s: StepSchema) => {
                return s.sessionKey.includes(data.stepSelector.replace('-', '_'));
            });

            subSteps = this.addStepFromRoute(route, subSteps, schemaStep);
        };

        this.environment.googleMapsAPI = stepsSchema.googleMapsApiKey;
        if (stepsSchema.maxFilesizeSum) {
            this.environment.productsConfig[this.product].maxBulkFilesSizeUpload = stepsSchema.maxFilesizeSum;
        }

        if (stepsSchema.maxFileSizeSingle) {
            this.environment.productsConfig[this.product].maxSingleFileSizeUpload = stepsSchema.maxFileSizeSingle;
        }

        if (stepsSchema.redirectedFromProperty) {
            this.redirectedFromProperty = stepsSchema.redirectedFromProperty;
        }

        Object.keys(subSteps).forEach((key: string) => {
            this._steps.push(subSteps[key]);
        });

        if (this.redirectedFromProperty) {
            this.originSteps = this._steps.slice();
            this.productRoutes.splice(0, 1);
            this._steps.splice(0, 1);
        }

        this.currentStepIndex = 0;
        if (this.backFromLiability) {
            this.currentStepIndex = 6;
        }

        this.setStep();
        this.stepsReady.next(true);
    }

    /**
     * Load current step schema and prepare widgets
     * @returns Subscription
     */
    loadCurrentStep(stepForm: StepForm) {
        this.registerWidgets(
            this.currentStep.form, stepForm.schema.properties, stepForm.session[this.currentStep.sessionKey], stepForm.schema.required);

        this.currentStep.sessionValues =
            stepForm.session[this.currentStep.sessionKey] ? stepForm.session[this.currentStep.sessionKey] : null;
        this.currentStep.optional = stepForm.nextStep != undefined ? true : false;
        this.currentStep.sessionSummary = this.currentStep.stepSelector == 'summary' ? stepForm.session : null;

        this.contentReady.next(true);
    }

    private registerWidgets(form: FormGroup, properties: StepFormProperties, values: StepFormSessionValues, requiredFields: Array<string>, parent?: string, grandParent?: string): FormGroup {

        let widgets = this.currentStep.widgets;
        let parentWidget: Widget | undefined;

        if (parent != undefined) {
            if (grandParent != undefined) {
                let gParent = this.currentStep.widgets[grandParent];
                if (gParent && gParent.widgets) {
                    gParent.widgets[parent].widgets = {} as Widgets;
                    widgets = gParent.widgets[parent].widgets as Widgets;
                    parentWidget = gParent.widgets[parent];
                }

            } else {
                this.currentStep.widgets[parent].widgets = {} as Widgets;
                widgets = this.currentStep.widgets[parent].widgets as Widgets;
                parentWidget = this.currentStep.widgets[parent];
            }
        }

        Object.keys(properties).forEach(fieldName => {
            let value = null;
            if (values != undefined) {
                value = values[fieldName];
                if (parent && values[parent] != null) {
                    value = values[parent][fieldName];
                }
            }

            if ((value as Address) != null && (value as Address).abbreviation) {
                value = (value as Address).abbreviation;
            }

            if ((value as City) != null && (value as City).id) {
                value = (value as City);
            }

            if (Array.isArray(value)) {
                if (typeof value[0] != 'object') {
                    value = value.map(num => {
                        return num.toString();
                    });
                }
            }

            if (typeof value == 'number') {
                value = value.toString();
            }

            let control: FormControl | FormGroup = new FormControl(value, null);
            const subProperties = properties[fieldName].properties;
            const subPropertiesItems = properties[fieldName].items?.properties;
            if (subProperties != undefined) {
                control = new FormGroup({});

                this.addWidget(widgets, fieldName, properties[fieldName], form, parentWidget);
                if (form instanceof FormGroup) {
                    let grandParent: string | undefined;
                    if (parentWidget) {
                        grandParent = parentWidget.name;
                    };

                    form.addControl(fieldName,
                        this.registerWidgets(control, subProperties, values, requiredFields, fieldName, grandParent),
                        { emitEvent: true }
                    );
                }

                return false;
            }

            if (properties[fieldName].widget == 'array' && subPropertiesItems) {
                control = new FormGroup({});

                this.addWidget(widgets, fieldName, properties[fieldName], form, parentWidget);
                const formArray: FormArray = new FormArray<FormGroup | FormControl>([
                    this.registerWidgets(control, subPropertiesItems, values, requiredFields, fieldName)
                ]);

                if (Array.isArray(value) && value.length > 0) {
                    for (let index = 0; index < value.length; index++) {
                        if (typeof value == 'object') {
                            for (const key in value[index]) {
                                if ((value[index][key] as Address) != null && (value[index][key] as Address).abbreviation) {
                                    value[index][key] = (value[index][key] as Address).abbreviation;
                                }
                            }

                            if (formArray.at(index) == undefined) {
                                formArray.push(
                                    this.copyFormGroup(formArray.at(0) as FormGroup)
                                );
                            }

                            formArray.at(index).patchValue(value[index]);
                        }
                    }
                }

                if (form.get(fieldName)) {
                    form.removeControl(fieldName);
                }

                form.addControl(fieldName, formArray,
                    { emitEvent: true }
                );

                return false;
            }


            this.addWidget(widgets, fieldName, properties[fieldName], form, parentWidget);
            form.addControl(fieldName, control, { emitEvent: true });

            return false;
        });

        return form;
    }

    private copyFormGroup(formGroup: FormGroup) {
        let newFormGroup = new FormGroup({});

        Object.keys(formGroup.controls).forEach(name => {
            if (formGroup.controls[name] instanceof FormGroup) {
                const controlGroup = formGroup.controls[name] as FormGroup;
                newFormGroup.registerControl(name, this.copyFormGroup(controlGroup));
                return;
            }

            newFormGroup.registerControl(name, new FormControl());
        });

        return newFormGroup;
    }


    private addWidget(widgets: Widgets, name: string, properties: StepFormProperty, form: FormGroup | FormArray, parentWidget?: Widget) {

        widgets[name] = {
            name: name,
            form: form,
            property: properties,
            parent: parentWidget,
            itemIndex: (form instanceof FormArray) ? 0 : undefined
        } as Widget;

    }

    get isDeployedIntoAnotherPage(): boolean {
        return (document as any).aemIntegrationAppServerURL !== undefined;
    }

    get deployUrl(): string {
        return this._deployUrl;
    }

    get unUploadedDocuments(): number {
        return this._unUploadedDocuments;
    }

    set unUploadedDocuments(count: number) {
        this._unUploadedDocuments = count;
    }

    public resetForwardSteps(index: number = 0) {
        index++;
        for (index; index < this.steps.length; index++) {
            // if (index == 0 && !this.redirectedFromProperty) {
            //     continue;
            // }
            this.steps[index].widgets = {};
            this.steps[index].form = new FormGroup({});
        }
    }

    /**
    * Set currentStep by currentStepIndex variable
    */
    setStep() {
        if (this._steps[this.currentStepIndex]) {
            this.currentStep = this._steps[this.currentStepIndex];
        }
    }

    get currentStep(): Step {
        return this._currentStep;
    }

    set currentStep(CurrentStep: Step) {
        this._currentStep = CurrentStep;
    }

    getCurrentStepIndex(): number {
        return this.currentStepIndex != null ? this.currentStepIndex : 0;
    }

    setCurrentStepIndex(index: number) {
        if (index < 0 || index > this.steps.length) {
            return;
        }

        this.currentStepIndex = index;
    }

    goToNextStep(): void {
        if (this.currentStepIndex == this.steps.length) {
            return;
        }

        this.currentStepIndex++;
        this.setStep();
        if (this.currentStep.skip == true) {
            // this.currentStepIndex++;
            // this.setStep();
            this.goToNextStep();
        }

        this.nextStepEvent.emit(true);
    }

    goToPreviousStep(): void {
        if (this.currentStepIndex == 0) {
            if (this.redirectedFromProperty) {
                this.currentStepIndex = -1
                this.previousStepEvent.emit(true);
            }
            return;
        }

        this.currentStepIndex--;
        this.setStep();
        if (this.currentStep.skip == true) {
            // this.currentStepIndex--;
            // this.setStep();
            this.goToPreviousStep();
        }

        this.previousStepEvent.emit(true);
    }

    get propertyDamage(): StrKeyValType | undefined {
        return this._propertyDamage;
    }

    set propertyDamage(pd: StrKeyValType | undefined) {
        this._propertyDamage = pd;
    }

    get documentUrl(): string | false {
        return this._documentUrl;
    }

    set documentUrl(url: string | false) {
        this._documentUrl = url;
    }

    get claimNumber(): string {
        return this._claimNumber;
    }

    set claimNumber(claimNumber: string) {
        this._claimNumber = claimNumber;
    }

    get steps(): Array<Step> {
        return this._steps;
    }

    get isLastStep(): boolean {
        return this.currentStep.stepSelector == 'summary';
    }

    // DRY
    conditionalDisplay(widget: Widget): boolean {
        if (!widget) {
            return false;
        }

        const attr = widget.property.attr;
        if (!attr) {
            return false;
        }

        if (attr['data-controller'] !== 'depends-on' || attr['data-depends-on-ng'] === undefined) {
            return false;
        }

        const dependsOnValue = attr['data-depends-value'] != undefined ? attr['data-depends-value'] : true;
        let control: AbstractControl | null = widget.form.get(attr['data-depends-on-ng']);
        if (control == null && widget.parent != undefined) {
            control = widget.parent.form.get(attr['data-depends-on-ng']);
        }

        return this.compare(control?.value, dependsOnValue);
    }

    // DRY
    private compare(value: any, dependsOnValue: any): boolean {

        if (Array.isArray(value)) {
            return value.includes(dependsOnValue.toString());
        }

        return value == dependsOnValue;
    }

    getWidgetValue(widget: Widget, subWidget?: string): string {
        if (subWidget && widget.widgets) {
            return widget.widgets[subWidget].form.get(subWidget)?.value;
        }
        return widget.form.get(widget.name)?.value;
    }


    /**
     * Update step with SchemaStep data
     * @param route
     * @param schemaStep
     */
    private addStepFromRoute(route: Route, subSteps: Steps, schemaStep?: StepSchema): Steps {

        if (route.data != undefined && route.data['stepSelector'] != undefined) {
            subSteps[route.data['stepSelector']] = {
                title: schemaStep?.title,
                slug: route.path,
                stepSelector: route.data['stepSelector'],
                showInProgressBar: route.data['showInProgressBar'],
                endpoint: schemaStep?.url,
                sessionKey: schemaStep?.sessionKey,
                skip: false,
                form: new FormGroup({}),
                widgets: {} as Widgets,
            } as Step;
        }

        return subSteps;
    }
}
